import { HttpClient, HttpContext, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { DvDropdownEvent } from '@devinco/ui/legacy';
import { environment } from '@env/environment';
import { FilterModelWithKeys } from '@features/vt-grid/interfaces/grid-context';
import { TranslateService } from '@ngx-translate/core';
import { GetTripsRes } from '@pages/trips-page/components/merge-trip-tab/interfaces';
import { GetTripPointsRes, MergeTripsReq } from '@pages/trips-page/interfaces';
import DynamicFormUtils from '@shared/utils/dynamic-form.utils';
import { IServerSideGetRowsRequest } from 'ag-grid-community';
import { Observable, take } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class RestApiService {
  private http = inject(HttpClient);
  private translate = inject(TranslateService);

  /** Endpoint mostly used by grids */
  getData<T>(request: GetRowsRequest): Observable<GetRowsResponse> {
    const url = `${environment.endpoints.restApi}/getRows`;
    const body = JSON.stringify(request);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<GetRowsResponse<T>>(url, body, options);
  }

  getInfo<T>(
    entity: string,
    entityId: string,
    fields: string[]
  ): Observable<T> {
    const url = `${environment.endpoints.restApi}/getInfo/${entity}/${entityId}`;
    const body = JSON.stringify(fields);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<T>(url, body, options);
  }

  /** Endpoint for updating entity by `id` */
  put(
    entity: string,
    entityId: string | number,
    data: object
  ): Observable<object> {
    const url = `${environment.endpoints.restApi}/put/${entity}/${entityId}`;
    const body = JSON.stringify(data);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.put(url, body, options);
  }

  upsert(
    entity: string,
    entityId: string | number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    queryParams?: string,
    context?: HttpContext
  ): Observable<number | undefined> {
    let url = `${environment.endpoints.restApi}/upsert/${entity}/${entityId}`;

    if (queryParams) {
      url += `?${queryParams}`;
    }

    const body = JSON.stringify(data);
    const options = {
      context,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<number | undefined>(url, body, options);
  }

  upsertMany<T>(entity: string, data: object[]): Observable<T> {
    const url = `${environment.endpoints.restApi}/upsertMany/${entity}`;
    const body = JSON.stringify(data);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<T>(url, body, options);
  }

  putCustom<T>(url: string, data?: object): Observable<T> {
    const fullUrl = `${environment.endpoints.restApi}/${url}`;
    const body = JSON.stringify(data);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.put<T>(fullUrl, body, options);
  }

  getCustom<T>(url: string, restApi?: string, options?: object): Observable<T> {
    const fullUrl = `${restApi ? restApi : environment.endpoints.restApi}/${url}`;
    let requestOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    if (options) {
      requestOptions = { ...requestOptions, ...options };
    }
    return this.http.get<T>(fullUrl, requestOptions);
  }

  /** Endpoint for creating new entity */
  post<T>(entity: string, data: object): Observable<T> {
    const url = `${environment.endpoints.restApi}/post/${entity}`;
    const body = JSON.stringify(data);
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<T>(url, body, options);
  }

  postCustom<T>(url: string, data?: unknown, options?: object): Observable<T> {
    const fullUrl = `${environment.endpoints.restApi}/${url}`;
    const body = JSON.stringify(data);
    let requestOptions = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };

    if (options) {
      requestOptions = { ...requestOptions, ...options };
    }

    return this.http.post<T>(fullUrl, body, requestOptions);
  }

  delete(
    entity: string,
    entityId: number,
    queryParams?: string
  ): Observable<object> {
    let url = `${environment.endpoints.restApi}/delete/${entity}/${entityId}`;

    if (queryParams) {
      url += `?${queryParams}`;
    }
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.delete(url, options);
  }

  deleteCustom(url: string, body?: unknown): Observable<object> {
    const fullUrl = `${environment.endpoints.restApi}/${url}`;
    const options = {
      body,
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.delete(fullUrl, options);
  }

  getTrips(entityId: string): Observable<GetTripsRes> {
    const url = `${environment.endpoints.restApi}/Trip/merge/gettrips/${entityId}`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<GetTripsRes>(url, {}, options);
  }

  getTripPoints(tripIds: number[]): Observable<GetTripPointsRes[] | undefined> {
    const url = `${environment.endpoints.restApi}/Trip/merge/gettrippoints`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<GetTripPointsRes[] | undefined>(
      url,
      tripIds,
      options
    );
  }

  addTranslation(key: string, value: string): Observable<number> {
    const url = 'https://prod-translation-api.azurewebsites.net/Text';
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    const payload = {
      key,
      reviewed: 0,
      productId: 3,
      value,
      language: this.translate.currentLang,
    };

    return this.http.post<number>(url, payload, options);
  }

  mergeTrips(mergeTripsReq: MergeTripsReq): Observable<number> {
    const url = `${environment.endpoints.restApi}/Trip/merge/mergetrips`;
    const options = {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
    };
    return this.http.post<number>(url, mergeTripsReq, options);
  }

  /** Set init dropdown data, using getRows endpoint */
  setDropdownData(
    request: GetRowsRequest,
    params: DvDropdownEvent,
    /** optional. Extra function to remap getRows response data for dropdown */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataMapFn?: (data: any) => any
  ): void {
    const items = params.items as undefined | unknown[];
    const { api, control } = params;

    const initialItemsCount = params.control?.value?.length;
    if (
      !items ||
      items.length <= 1 ||
      (items.length && items.length === initialItemsCount) ||
      !control?.touched
    ) {
      control?.markAsUntouched();
      api.setIsLoading(true);
      this.getData(request).subscribe(res => {
        api.setIsLoading(false);

        if (dataMapFn) {
          res.data = dataMapFn(res.data);
        }

        api.setItems(res.data);
        control?.markAsTouched();
      });
    }
  }

  /** Set init dropdown with translated data, using getRows endpoint */
  setTranslatedDropdownData<TData = any, TMapped = any>(
    request: GetRowsRequest,
    params: DvDropdownEvent,
    translationKeyParamName: string,
    translationPrefix?: string,
    translationSuffix?: string,
    dataMapFn?: (data: TData[]) => TMapped[]
  ): void {
    const items = params.items as undefined | unknown[];
    const { api, control } = params;
    const initialItemsCount = params.control?.value?.length;

    if (
      !items ||
      items.length <= 1 ||
      (items.length && items.length === initialItemsCount) ||
      !params.control?.touched
    ) {
      control?.markAsUntouched();
      api.setIsLoading(true);
      this.getData(request)
        .pipe(take(1))
        .subscribe(res => {
          if (dataMapFn) {
            res.data = dataMapFn(res.data);
          }

          const translatedItems = DynamicFormUtils.translateDropdownData<
            Record<string, unknown>
          >(
            res.data,
            this.translate,
            params.labelKeys![0] as string,
            translationKeyParamName,
            translationPrefix,
            translationSuffix
          );
          api.setIsLoading(false);
          api.setItems(translatedItems);
          control?.markAsTouched();
        });
    }
  }
}

export interface GetRowsRequest<
  T = any,
> extends Partial<IServerSideGetRowsRequest> {
  entity: string;
  fields: (keyof T)[];
  filterModel?: FilterModelWithKeys;
  quickFilterText?: string;
  /** used for custom filtering that's not in ag-grid's api scope (trips grid) */
  customFilter?: object;
  /** array of required field names to get sum calculations from BE */
  sum?: string[];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface GetRowsResponse<TData = any> {
  data: TData;
  totalCount: number;
  /** object of fields with sum values that were requested in GetRowsRequest */
  sum?: object;
}
